/* eslint-disable react/prop-types */
"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";
import AppModalShell, { AppModalButton } from "../common/AppModalShell";
import PremiumCodeLabel from "../common/PremiumCodeLabel";
import EventToolsBackButton from "./EventToolsBackButton";
import { resolveUserAvatarSrc } from "../../utils/userAvatar";

const normalizeCode = (value) => String(value || "").trim().toUpperCase();

const getUserId = (participant) =>
  Number(participant?.user_id || participant?.user?.id || participant?.id || 0);

const toAvatarSrc = (participant) =>
  resolveUserAvatarSrc(participant, "/assets/events/default_avatar.png");

// This modal is also mounted after the parent tools panel has already animated in.
// Give its action buttons an explicit visible state instead of inheriting a stale
// "hidden" variant from that parent.
const FORCE_VISIBLE_MOTION_PROPS = {
  initial: false,
  animate: { opacity: 1, y: 0, scale: 1 },
};

const RemoveParticipantModal = ({
  visible = false,
  eventId = null,
  participants = [],
  hostCode = "",
  viewerCode = "",
  onClose = () => {},
  onBack = null,
  onRemoved = null,
  embedded = false,
}) => {
  const { t } = useTranslation("events");
  const [selectedUserIds, setSelectedUserIds] = React.useState([]);
  const [confirming, setConfirming] = React.useState(false);
  const [reason, setReason] = React.useState("");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [localError, setLocalError] = React.useState("");

  const normalizedParticipants = React.useMemo(() => {
    const host = normalizeCode(hostCode);
    const viewer = normalizeCode(viewerCode);
    const seen = new Set();
    const result = [];

    for (const participant of Array.isArray(participants) ? participants : []) {
      const userId = getUserId(participant);
      const code = normalizeCode(participant?.code || participant?.user?.code);
      if (!userId || !code || seen.has(userId)) continue;
      if (participant?.is_self || code === host || code === viewer) continue;
      seen.add(userId);
      result.push({
        ...participant,
        user_id: userId,
        code,
        avatar: participant?.avatar || participant?.user?.avatar || "",
        avatar_custom_url:
          participant?.avatar_custom_url ||
          participant?.user?.avatar_custom_url ||
          null,
        has_premium_subscription: Boolean(
          participant?.has_premium_subscription ||
            participant?.user?.has_premium_subscription
        ),
      });
    }

    return result.sort((a, b) => a.code.localeCompare(b.code));
  }, [hostCode, participants, viewerCode]);

  const participantById = React.useMemo(() => {
    const map = new Map();
    normalizedParticipants.forEach((participant) => {
      map.set(participant.user_id, participant);
    });
    return map;
  }, [normalizedParticipants]);

  const filteredParticipants = React.useMemo(() => {
    const query = normalizeCode(searchQuery);
    if (!query) return normalizedParticipants;
    return normalizedParticipants.filter((participant) =>
      normalizeCode(participant.code).includes(query)
    );
  }, [normalizedParticipants, searchQuery]);

  const selectedParticipants = selectedUserIds
    .map((userId) => participantById.get(userId))
    .filter(Boolean);

  React.useEffect(() => {
    if (!visible) {
      setSelectedUserIds([]);
      setConfirming(false);
      setReason("");
      setSearchQuery("");
      setSubmitting(false);
      setLocalError("");
      return;
    }

    setSelectedUserIds((current) => {
      const next = current.filter((userId) => participantById.has(userId));
      return next.length === current.length ? current : next;
    });
  }, [participantById, visible]);

  if (!visible) return null;

  const selectedCount = selectedParticipants.length;
  const trimmedReason = reason.trim();
  const canConfirmRemoval =
    Boolean(eventId) &&
    selectedCount > 0 &&
    trimmedReason.length >= 10 &&
    !submitting;

  const toggleParticipant = (userId) => {
    if (submitting) return;
    setLocalError("");
    setSelectedUserIds((current) =>
      current.includes(userId)
        ? current.filter((value) => value !== userId)
        : [...current, userId]
    );
  };

  const handleBack = () => {
    if (submitting) return;
    if (confirming) {
      setConfirming(false);
      setLocalError("");
      return;
    }
    if (typeof onBack === "function") {
      onBack();
    } else {
      onClose();
    }
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleStartConfirm = () => {
    if (!selectedCount || submitting) return;
    setConfirming(true);
    setLocalError("");
  };

  const submitRemoval = async () => {
    if (!canConfirmRemoval) return;

    setSubmitting(true);
    setLocalError("");
    const removedIds = [];
    const errors = [];
    let moderationCount = 0;

    for (const participant of selectedParticipants) {
      try {
        const formData = new FormData();
        formData.append("reason", trimmedReason);
        const { data } = await api.post(
          `/chats/${eventId}/internal-chat/kick/${participant.user_id}/`,
          formData
        );

        if (data?.moderation_required) {
          moderationCount += 1;
        } else {
          removedIds.push(participant.user_id);
        }
      } catch (err) {
        errors.push(err);
      }
    }

    try {
      if (removedIds.length && typeof onRemoved === "function") {
        await onRemoved(
          removedIds,
          selectedParticipants.filter((participant) =>
            removedIds.includes(participant.user_id)
          )
        );
      }

      if (errors.length) {
        const firstError = errors[0];
        const status = firstError?.response?.status;
        const message =
          firstError?.response?.data?.error ||
          firstError?.message ||
          t("events.details.more_actions.remove_participant_modal.generic_error");
        setLocalError(message);
        window.dispatchEvent(
          new CustomEvent("global-error", { detail: { message, status } })
        );
      }

      if (removedIds.length) {
        window.dispatchEvent(
          new CustomEvent("global-info", {
            detail: {
              message: t(
                removedIds.length === 1
                  ? "events.details.more_actions.remove_participant_modal.success_one"
                  : "events.details.more_actions.remove_participant_modal.success_many",
                { count: removedIds.length }
              ),
            },
          })
        );
      }

      if (moderationCount > 0) {
        window.dispatchEvent(
          new CustomEvent("global-info", {
            detail: {
              message: t(
                moderationCount === 1
                  ? "events.details.more_actions.remove_participant_modal.moderation_one"
                  : "events.details.more_actions.remove_participant_modal.moderation_many",
                { count: moderationCount }
              ),
            },
          })
        );
      }

      if (removedIds.length || moderationCount > 0) {
        onClose();
      }
    } catch (err) {
      const status = err?.response?.status;
      const message =
        err?.response?.data?.error ||
        err?.message ||
        t("events.details.more_actions.remove_participant_modal.generic_error");
      setLocalError(message);
      window.dispatchEvent(
        new CustomEvent("global-error", { detail: { message, status } })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const modalTitle = confirming
    ? t("events.details.more_actions.remove_participant_modal.confirm_title")
    : t("events.details.more_actions.remove_participant_modal.title");
  const modalDescription = confirming
    ? t("events.details.more_actions.remove_participant_modal.confirm_body", {
        count: selectedCount,
      })
    : t("events.details.more_actions.remove_participant_modal.description");

  const footer = confirming ? (
    <div className="flex gap-3">
      <AppModalButton
        variant="secondary"
        onClick={() => setConfirming(false)}
        disabled={submitting}
        className="flex-1"
        {...FORCE_VISIBLE_MOTION_PROPS}
      >
        {t("events.details.more_actions.remove_participant_modal.cancel")}
      </AppModalButton>
      <AppModalButton
        variant="danger"
        onClick={submitRemoval}
        disabled={!canConfirmRemoval}
        loading={submitting}
        className="flex-1"
        {...FORCE_VISIBLE_MOTION_PROPS}
      >
        {submitting
          ? t("events.details.more_actions.remove_participant_modal.removing")
          : t("events.details.more_actions.remove_participant_modal.confirm")}
      </AppModalButton>
    </div>
  ) : (
    <AppModalButton
      variant="danger"
      onClick={handleStartConfirm}
      disabled={!selectedCount || submitting}
      {...FORCE_VISIBLE_MOTION_PROPS}
    >
      {selectedCount
        ? t("events.details.more_actions.remove_participant_modal.remove_selected", {
            count: selectedCount,
          })
        : t("events.details.more_actions.remove_participant_modal.remove")}
    </AppModalButton>
  );

  const bodyContent = (
    <>
      {confirming ? (
        <div className="space-y-4">
          <div className="flex flex-wrap justify-center gap-3">
            {selectedParticipants.map((participant) => (
              <div key={`confirm-${participant.user_id}`} className="flex flex-col items-center">
                <img
                  src={toAvatarSrc(participant)}
                  alt={participant.code}
                  className="h-[55px] w-[55px] rounded-full object-cover ring-2 ring-white/80"
                />
                <PremiumCodeLabel
                  as="div"
                  code={participant.code}
                  isPremium={participant.has_premium_subscription}
                  className="mt-1 text-base font-extrabold leading-none text-white"
                  outlineClassName="rounded-[4px] px-[3px] py-0"
                />
              </div>
            ))}
          </div>

          <div>
            <label className="block text-sm font-semibold text-white/90">
              {t("events.details.more_actions.remove_participant_modal.reason_label")}
            </label>
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t("events.details.more_actions.remove_participant_modal.reason_placeholder")}
              className="mt-2 min-h-[118px] w-full resize-none rounded-[18px] border border-white/12 bg-white/10 px-4 py-3 text-sm font-medium leading-5 text-white outline-none placeholder:text-white/42"
            />
            <p className="mt-2 text-xs font-medium text-white/65">
              {t("events.details.more_actions.remove_participant_modal.reason_min")}
            </p>
          </div>
        </div>
      ) : normalizedParticipants.length === 0 ? (
        <div className="rounded-[18px] border border-white/10 bg-white/[0.07] px-4 py-5 text-center text-sm font-medium text-white/78">
          {t("events.details.more_actions.remove_participant_modal.empty")}
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={t("events.details.more_actions.remove_participant_modal.search_placeholder")}
              className="h-11 w-full rounded-full border border-white/12 bg-white/10 px-4 text-sm font-semibold text-white outline-none placeholder:text-white/42"
              autoComplete="off"
            />
          </div>

          {filteredParticipants.length === 0 ? (
            <div className="rounded-[18px] border border-white/10 bg-white/[0.07] px-4 py-5 text-center text-sm font-medium text-white/78">
              {t("events.details.more_actions.remove_participant_modal.no_results")}
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-2.5 sm:grid-cols-6">
              {filteredParticipants.map((participant) => {
                const selected = selectedUserIds.includes(participant.user_id);
                return (
                  <button
                    key={participant.user_id}
                    type="button"
                    onClick={() => toggleParticipant(participant.user_id)}
                    disabled={submitting}
                    className={`flex min-h-[92px] flex-col items-center justify-center rounded-[16px] px-1.5 py-2 transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 ${
                      selected
                        ? "bg-white/[0.13] shadow-[0_12px_28px_rgba(0,0,0,0.18)]"
                        : "bg-transparent hover:bg-white/[0.055]"
                    }`}
                    aria-pressed={selected}
                  >
                    <img
                      src={toAvatarSrc(participant)}
                      alt={participant.code}
                      className={`h-[52px] w-[52px] rounded-full object-cover ${
                        selected
                          ? "ring-2 ring-[#8BBCFF]"
                          : participant.avatar_custom_url
                          ? "ring-2 ring-white/70"
                          : ""
                      }`}
                    />
                    <PremiumCodeLabel
                      as="div"
                      code={participant.code}
                      isPremium={participant.has_premium_subscription}
                      className="mt-2 max-w-full truncate text-base font-extrabold leading-none text-white"
                      outlineClassName="rounded-[4px] px-[3px] py-0"
                    />
                  </button>
                );
              })}
            </div>
          )}

          <div className="text-center text-sm font-semibold text-white/72">
            {selectedCount > 0
              ? t("events.details.more_actions.remove_participant_modal.selected_count", {
                  count: selectedCount,
                })
              : t("events.details.more_actions.remove_participant_modal.select_hint")}
          </div>
        </div>
      )}

      {localError ? (
        <div className="mt-4 rounded-[18px] border border-red-300/25 bg-red-500/18 px-3 py-2 text-sm font-medium text-red-50">
          {localError}
        </div>
      ) : null}
    </>
  );

  if (embedded) {
    return (
      <>
        <p className="mb-5 text-center text-[15px] font-medium leading-7 text-white/88">
          {modalDescription}
        </p>
        {bodyContent}
        <div className="mt-5">{footer}</div>
      </>
    );
  }

  return (
    <AppModalShell
      open={visible}
      title={modalTitle}
      description={modalDescription}
      onClose={handleClose}
      closeAriaLabel={t("events.details.more_actions.remove_participant_modal.close")}
      zIndexClassName="z-[1200]"
      maxWidthClassName="max-w-[640px]"
      descriptionClassName="text-center"
      leftAction={
        <EventToolsBackButton
          onClick={handleBack}
          disabled={submitting}
          label={t("events.details.more_actions.back")}
        />
      }
      footer={footer}
    >
      {bodyContent}
    </AppModalShell>
  );
};

export default RemoveParticipantModal;
